import { guardDialog, promptDialog } from '@adornis/dialog/dialog-functions.js';
import type { SectionRendererSection } from '@adornis/entity-relationship-management/client/details/SectionRenderer.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { stopImpersonation } from '@adornis/users/client/impersonation.js';
import { _subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { html, nothing } from 'lit';
import { nexusImpersonateUser, setNexusUserPassword } from '../../api/nexus-user-authentication.js';

const AuthenticationEditor = stateful(({ user }: { user: AdornisUser }) => {
  const handlePasswordReset = async () => {
    const newPassword = await promptDialog(`New password for ${user.email ?? user._id}?`, {
      placeholder: '********',
      buttonText: 'Save',
      hidden: true,
    }).catch(() => null);
    if (!newPassword) return;
    await guardDialog(() => setNexusUserPassword(user._id, newPassword)(), {
      loadingText: 'Updating password…',
      successText: 'Password updated.',
      errorMessagePrefix: 'Could not update password',
      showLoaderModal: true,
    });
  };

  const handleImpersonate = async () => {
    await guardDialog(
      async () => {
        await nexusImpersonateUser(user._id)();
        await _subscribeCurrentUser();
      },
      {
        hasToConfirm: true,
        confirmText: `Really impersonate ${user.email ?? user.username ?? user._id}?`,
        loadingText: 'Starting impersonation…',
        successText: `You are now logged in as ${user.email ?? user.username ?? user._id}.`,
        errorMessagePrefix: 'Impersonation failed',
      },
    );
  };

  const handleStopImpersonation = async () => {
    await guardDialog(
      async () => {
        await stopImpersonation();
        globalThis.location.assign('/admin/users');
      },
      {
        loadingText: 'Ending impersonation…',
        successText: 'Impersonation ended.',
        errorMessagePrefix: 'Could not end impersonation',
        showLoaderModal: false,
      },
    );
  };

  return html`
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <p class="text-sm font-semibold text-base-content">Change password</p>
        <p class="text-xs text-base-content/70">Set a new password for this user.</p>
        <button class="btn btn-primary btn-sm gap-2 self-start" @click=${handlePasswordReset}>
          <span class="icon-[material-symbols--key-vertical] text-lg"></span>
          Reset password
        </button>
      </div>
      <div class="flex flex-col gap-2">
        <p class="text-sm font-semibold text-base-content">Impersonation</p>
        <p class="text-xs text-base-content/70">Switch into or out of the user's session.</p>
        <div class="flex flex-wrap gap-2">
          <button class="btn btn-outline btn-sm gap-2" @click=${handleImpersonate}>
            <span class="icon-[material-symbols--switch-account] text-lg"></span>
            Impersonate user
          </button>
          <button class="btn btn-ghost btn-sm gap-2" @click=${handleStopImpersonation}>
            <span class="icon-[material-symbols--person-off-outline] text-lg"></span>
            End impersonation
          </button>
        </div>
      </div>
    </div>
  `;
});

export const nexusAuthenticationSection: SectionRendererSection<AdornisUser> = {
  title: 'Authentication',
  fields: [{ render: ({ entity: user }) => (!user ? nothing : AuthenticationEditor({ user })) }],
};
